import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function AdminRecharges() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [startDT, setStartDT] = useState("");
  const [endDT, setEndDT] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/recharges");
      setList(data);
    })();
  }, []);

  const filtered = list.filter((r) => {
    if (
      filter &&
      !r.reseller_email?.toLowerCase().includes(filter.toLowerCase()) &&
      !r.recipient_uid?.toLowerCase().includes(filter.toLowerCase())
    )
      return false;
    const t = new Date(r.created_at).getTime();
    if (startDT && t < new Date(startDT).getTime()) return false;
    if (endDT && t > new Date(endDT).getTime()) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-recharges-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Audit</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">All Recharges</h1>
        <p className="text-zinc-500 mt-1">Complete recharge log across all resellers.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="recharge-filters">
        <input
          type="text"
          placeholder="Search by reseller email or recipient UID"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 min-w-[220px] max-w-md h-10 px-3 py-2 text-sm rounded-md border border-zinc-300 bg-white"
          data-testid="recharge-filter"
        />
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">From</span>
          <input
            type="datetime-local"
            value={startDT}
            max={endDT || undefined}
            onChange={(e) => setStartDT(e.target.value)}
            className="h-10 px-2 text-sm rounded-md border border-zinc-300 bg-white"
            data-testid="recharge-start-date"
          />
          <span className="text-xs text-zinc-500">To</span>
          <input
            type="datetime-local"
            value={endDT}
            min={startDT || undefined}
            onChange={(e) => setEndDT(e.target.value)}
            className="h-10 px-2 text-sm rounded-md border border-zinc-300 bg-white"
            data-testid="recharge-end-date"
          />
          {(startDT || endDT) && (
            <button
              onClick={() => { setStartDT(""); setEndDT(""); }}
              className="h-10 px-3 rounded-md text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
              data-testid="recharge-clear-dates"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <Th>Time</Th>
              <Th>Reseller</Th>
              <Th>Platform</Th>
              <Th>Type</Th>
              <Th>Recipient UID</Th>
              <Th className="text-right">Diamonds</Th>
              <Th className="text-right">Balance after</Th>
              <Th>Status</Th>
              <Th>Mode</Th>
              <Th>Note</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="10" className="px-6 py-12 text-center text-zinc-400 text-sm">No matching recharges</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                <Td mono>{new Date(r.created_at).toLocaleString()}</Td>
                <Td>{r.reseller_email}</Td>
                <Td>{r.platform_name || "Trumeet"}</Td>
                <Td>
                  {r.type === "noble" ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800">
                      {r.tier_label || "Noble"}{r.mode === "renew" ? " · renew" : ""}
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-500">Transfer</span>
                  )}
                </Td>
                <Td mono>{r.recipient_uid}</Td>
                <Td className="text-right tabular-nums font-mono">{r.diamonds}</Td>
                <Td className="text-right tabular-nums font-mono">{r.balance_after == null ? "—" : r.balance_after.toLocaleString()}</Td>
                <Td>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                    r.status === "success" ? "bg-green-100 text-green-800" :
                    r.status === "failed" ? "bg-red-100 text-red-800" :
                    "bg-amber-100 text-amber-800"
                  }`}>{r.status}</span>
                </Td>
                <Td>
                  <span className="text-xs text-zinc-500 font-mono">{r.test_mode ? "test" : "live"}</span>
                </Td>
                <Td>{r.note || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3 ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "", mono }) => (
  <td className={`px-6 py-3 text-sm text-zinc-700 ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
);
